import { useLocation } from "wouter";
import { usePlan, navigateToUpgrade } from "@/hooks/usePlan";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Clock, TrendingUp, Zap } from "lucide-react";

const GREEN = "#22c55e";
const AMBER = "#f59e0b";
const RED = "#ef4444";
const BLUE = "#3b82f6";
const CARD_BORDER = "rgba(99,102,241,0.15)";
const MUTED = "#94a3b8";
const DARK_BG = "#05070a";

function barColour(pct: number): string {
  if (pct < 60) return GREEN;
  if (pct < 80) return AMBER;
  return RED;
}

function UsageBar({
  label,
  value,
  max,
  pct,
}: {
  label: string;
  value: number;
  max: number | null;
  pct: number;
}) {
  const colour = barColour(pct);
  const isUnlimited = max === null;
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div className="flex justify-between mb-1.5" style={{ fontSize: 12 }}>
        <span style={{ color: MUTED, fontWeight: 500 }}>{label}</span>
        <span style={{ color: "#f1f5f9", fontWeight: 600 }}>
          {isUnlimited ? "Unlimited" : `${value} / ${max}`}
        </span>
      </div>
      {!isUnlimited && (
        <div
          style={{
            height: 6,
            borderRadius: 999,
            background: "rgba(255,255,255,0.08)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${pct}%`,
              background: colour,
              borderRadius: 999,
              transition: "width 0.4s ease",
            }}
          />
        </div>
      )}
      <div style={{ fontSize: 11, color: MUTED, marginTop: 4 }}>
        {isUnlimited
          ? "No limit on this plan"
          : `${Math.max(0, (max ?? 0) - value)} remaining`}
      </div>
    </div>
  );
}

/* ─── COMPACT (Today tab) ─────────────────────────────────────── */
export function PlanUsageCompact() {
  const [, navigate] = useLocation();
  const enforcement = usePlan();
  const { plan, usage, isLoading, showUpgradePrompt, usagePercentRoles, usagePercentCandidates, isAtRoleLimit, isAtCandidateLimit } = enforcement;

  if (isLoading) return null;
  if (!showUpgradePrompt && !plan.isFree) return null;

  const hasAlert = isAtRoleLimit || isAtCandidateLimit;

  return (
    <div
      data-testid="plan-usage-compact"
      style={{
        border: `1px solid ${hasAlert ? "rgba(239,68,68,0.4)" : CARD_BORDER}`,
        borderRadius: 10,
        padding: "12px 16px",
        background: hasAlert ? "rgba(239,68,68,0.06)" : "rgba(99,102,241,0.05)",
        marginBottom: 16,
        display: "flex",
        alignItems: "center",
        gap: 16,
        flexWrap: "wrap",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, flex: "0 0 auto" }}>
        {hasAlert ? (
          <AlertTriangle className="w-4 h-4" style={{ color: RED, flexShrink: 0 }} />
        ) : (
          <Zap className="w-4 h-4" style={{ color: BLUE, flexShrink: 0 }} />
        )}
        <span style={{ fontSize: 13, fontWeight: 600, color: "#f1f5f9" }}>
          {plan.displayName}
        </span>
      </div>

      <div style={{ flex: 1, display: "flex", gap: 16, flexWrap: "wrap" }}>
        {plan.maxActiveRoles !== null && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 140 }}>
            <div
              style={{
                flex: 1,
                height: 5,
                borderRadius: 999,
                background: "rgba(255,255,255,0.08)",
                overflow: "hidden",
                minWidth: 60,
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: `${usagePercentRoles}%`,
                  background: barColour(usagePercentRoles),
                  borderRadius: 999,
                }}
              />
            </div>
            <span style={{ fontSize: 12, color: MUTED, whiteSpace: "nowrap" }}>
              {usage.activeRoles}/{plan.maxActiveRoles} roles
            </span>
          </div>
        )}

        {plan.maxCandidatesPerMonth !== null && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 160 }}>
            <div
              style={{
                flex: 1,
                height: 5,
                borderRadius: 999,
                background: "rgba(255,255,255,0.08)",
                overflow: "hidden",
                minWidth: 60,
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: `${usagePercentCandidates}%`,
                  background: barColour(usagePercentCandidates),
                  borderRadius: 999,
                }}
              />
            </div>
            <span style={{ fontSize: 12, color: MUTED, whiteSpace: "nowrap" }}>
              {usage.candidatesThisMonth}/{plan.maxCandidatesPerMonth} this month
            </span>
          </div>
        )}
      </div>

      <Button
        size="sm"
        data-testid="compact-upgrade-btn"
        onClick={() => navigateToUpgrade(navigate, hasAlert ? (isAtRoleLimit ? "role_limit" : "candidate_limit") : "approaching_limit", plan.tierName)}
        style={{
          background: hasAlert ? RED : BLUE,
          color: "#fff",
          border: "none",
          fontSize: 12,
          padding: "4px 12px",
          height: 30,
          flexShrink: 0,
        }}
      >
        Upgrade →
      </Button>
    </div>
  );
}

/* ─── FREE ROLE EXPIRY BANNER (reusable inline banner) ─────────── */
export function FreeRoleExpiryBanner({ compact = false }: { compact?: boolean }) {
  const { plan, usage } = usePlan();

  if (!plan.isFree || !usage.freeRoleExpiresAt) return null;

  const daysLeft = Math.ceil(
    (new Date(usage.freeRoleExpiresAt).getTime() - Date.now()) / 86400000
  );

  if (daysLeft <= 0) return null;

  const isUrgent = daysLeft <= 7;

  return (
    <div
      data-testid="free-role-expiry-banner-pipeline"
      className={`flex items-center justify-between rounded-lg px-4 py-3 mb-3 text-sm ${
        isUrgent
          ? "bg-red-50 border border-red-300 text-red-800"
          : "bg-amber-50 border border-amber-300 text-amber-800"
      }`}
    >
      <span>
        {isUrgent ? "🔴" : "⚠️"}{" "}
        <strong>Role expires in {daysLeft} day{daysLeft !== 1 ? "s" : ""}</strong>
        {" — "}
        {isUrgent
          ? "Upgrade now to keep your pipeline active."
          : "Upgrade to keep this role open."}
      </span>
      <a
        href={`/pricing?reason=role_expiry&currentPlan=free&recommended=starter`}
        className={`ml-4 text-xs font-semibold underline whitespace-nowrap ${
          isUrgent
            ? "text-red-700 hover:text-red-900"
            : "text-amber-700 hover:text-amber-900"
        }`}
      >
        Upgrade →
      </a>
    </div>
  );
}

/* ─── FULL (Settings page) ────────────────────────────────────── */
export function PlanUsageFull() {
  const [, navigate] = useLocation();
  const enforcement = usePlan();
  const {
    plan, usage, isLoading, error,
    showUpgradePrompt, upgradeReason,
    usagePercentRoles, usagePercentCandidates,
    daysUntilBillingRenewal, isTrialActive, trialDaysRemaining,
    isAtRoleLimit, isAtCandidateLimit,
  } = enforcement;

  if (isLoading) {
    return (
      <div style={{ color: MUTED, fontSize: 13, padding: 16 }}>Loading plan data…</div>
    );
  }

  if (error) {
    return (
      <div style={{ color: RED, fontSize: 13, padding: 16 }}>{error}</div>
    );
  }

  const formatDate = (iso: string | null) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("en-NZ", { day: "numeric", month: "long", year: "numeric" });
  };

  const tierBadgeColour = plan.isFree ? "#6b7280" : plan.isEnterprise ? "#8b5cf6" : BLUE;

  return (
    <div data-testid="plan-usage-full">
      {/* Free tier expiry banner */}
      {plan.isFree && usage.freeRoleExpiresAt && (() => {
        const daysLeft = Math.ceil(
          (new Date(usage.freeRoleExpiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
        );
        const urgent = daysLeft <= 7;
        return daysLeft > 0 ? (
          <div
            data-testid="free-expiry-banner"
            style={{
              border: `1px solid ${urgent ? "rgba(239,68,68,0.4)" : "rgba(245,158,11,0.35)"}`,
              borderRadius: 10,
              padding: "14px 16px",
              background: urgent ? "rgba(239,68,68,0.07)" : "rgba(245,158,11,0.07)",
              marginBottom: 20,
            }}
          >
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: urgent ? RED : AMBER }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: "#f1f5f9", marginBottom: 4 }}>
                  Free Plan — Role expires in {daysLeft} day{daysLeft !== 1 ? "s" : ""}
                </div>
                <div style={{ fontSize: 13, color: MUTED, lineHeight: 1.5, marginBottom: 10 }}>
                  You have {usage.activeRoles} active role and {usage.candidatesThisMonth} candidate assessment{usage.candidatesThisMonth !== 1 ? "s" : ""} on the free plan.
                  Candidates in your pipeline will be preserved.
                </div>
                <Button
                  size="sm"
                  data-testid="free-expiry-upgrade-btn"
                  onClick={() => navigateToUpgrade(navigate, "role_expiry", "free", "starter")}
                  style={{ background: AMBER, color: "#000", border: "none", fontWeight: 600, fontSize: 12 }}
                >
                  Upgrade to keep it open →
                </Button>
              </div>
            </div>
          </div>
        ) : null;
      })()}

      {/* Trial warning */}
      {isTrialActive && trialDaysRemaining !== null && trialDaysRemaining <= 7 && (
        <div
          data-testid="trial-expiry-banner"
          style={{
            border: `1px solid ${trialDaysRemaining <= 1 ? "rgba(239,68,68,0.4)" : "rgba(245,158,11,0.35)"}`,
            borderRadius: 10,
            padding: "12px 16px",
            background: trialDaysRemaining <= 1 ? "rgba(239,68,68,0.07)" : "rgba(245,158,11,0.07)",
            marginBottom: 20,
            fontSize: 13,
            color: "#f1f5f9",
          }}
        >
          Your free trial ends in <strong>{trialDaysRemaining} day{trialDaysRemaining !== 1 ? "s" : ""}</strong>.
          Add payment details to continue after{" "}
          {usage.trialEndsAt ? formatDate(usage.trialEndsAt) : "that date"}.{" "}
          <button
            style={{ color: BLUE, textDecoration: "underline", background: "none", border: "none", cursor: "pointer", padding: 0 }}
            onClick={() => navigate("/settings/billing")}
          >
            Add Payment Method →
          </button>
        </div>
      )}

      {/* Usage bars */}
      <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginBottom: 24 }}>
        <UsageBar
          label="Active Roles"
          value={usage.activeRoles}
          max={plan.maxActiveRoles}
          pct={usagePercentRoles}
        />
        <UsageBar
          label="Assessments This Month"
          value={usage.candidatesThisMonth}
          max={plan.maxCandidatesPerMonth}
          pct={usagePercentCandidates}
        />
      </div>

      {/* Upgrade reason */}
      {showUpgradePrompt && upgradeReason && (
        <div
          data-testid="upgrade-reason"
          style={{
            border: `1px solid rgba(239,68,68,0.3)`,
            borderRadius: 8,
            padding: "12px 14px",
            background: "rgba(239,68,68,0.06)",
            marginBottom: 20,
            fontSize: 13,
            color: "#f1f5f9",
            lineHeight: 1.5,
          }}
        >
          <AlertTriangle className="w-4 h-4 inline mr-2" style={{ color: RED, verticalAlign: "middle" }} />
          {upgradeReason}
        </div>
      )}

      {/* Billing info */}
      <div
        style={{
          border: `1px solid ${CARD_BORDER}`,
          borderRadius: 10,
          padding: "16px 18px",
          background: "rgba(255,255,255,0.02)",
        }}
      >
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-3">
            <span
              style={{
                background: tierBadgeColour,
                color: "#fff",
                fontSize: 11,
                fontWeight: 700,
                padding: "2px 10px",
                borderRadius: 999,
                letterSpacing: "0.04em",
              }}
            >
              {plan.displayName.toUpperCase()}
            </span>
            {plan.priceNzdMonthly !== null && (
              <span style={{ fontSize: 15, fontWeight: 600, color: "#f1f5f9" }}>
                {plan.priceNzdMonthly === 0 ? "Free" : `$${plan.priceNzdMonthly} NZD/mo`}
              </span>
            )}
            {plan.priceNzdMonthly === null && !plan.isFree && (
              <span style={{ fontSize: 13, color: MUTED }}>Custom pricing</span>
            )}
          </div>
          {!plan.isEnterprise && (
            <Button
              size="sm"
              data-testid="billing-upgrade-btn"
              onClick={() => navigateToUpgrade(navigate, "plan_upgrade", plan.tierName)}
              style={{ background: BLUE, color: "#fff", border: "none", fontSize: 12 }}
            >
              {plan.isFree ? "Start Free Trial" : "Upgrade Plan"}
            </Button>
          )}
        </div>

        <div style={{ display: "grid", gap: 8 }}>
          {usage.subscriptionRenewalDate && (
            <div className="flex justify-between" style={{ fontSize: 13 }}>
              <span style={{ color: MUTED }}>Next billing date</span>
              <span style={{ color: "#f1f5f9" }}>
                {formatDate(usage.subscriptionRenewalDate)}
                {daysUntilBillingRenewal !== null && daysUntilBillingRenewal <= 3 && (
                  <span style={{ color: AMBER, marginLeft: 8, fontSize: 11 }}>in {daysUntilBillingRenewal} days</span>
                )}
              </span>
            </div>
          )}

          {plan.maxTeamSeats !== null && (
            <div className="flex justify-between" style={{ fontSize: 13 }}>
              <span style={{ color: MUTED }}>Team seats</span>
              <span style={{ color: "#f1f5f9" }}>
                {usage.teamSeats} / {plan.maxTeamSeats} used
              </span>
            </div>
          )}
          {plan.maxTeamSeats === null && (
            <div className="flex justify-between" style={{ fontSize: 13 }}>
              <span style={{ color: MUTED }}>Team seats</span>
              <span style={{ color: "#f1f5f9" }}>Unlimited</span>
            </div>
          )}
        </div>

        <div className="flex gap-3 mt-4" style={{ flexWrap: "wrap" }}>
          <Button
            size="sm"
            variant="outline"
            data-testid="manage-billing-btn"
            onClick={async () => {
              const res = await fetch("/api/billing/portal", { method: "POST", credentials: "include" });
              const data = await res.json();
              if (data.url) window.location.href = data.url;
            }}
            style={{ fontSize: 12, borderColor: CARD_BORDER, color: "#f1f5f9" }}
          >
            Manage Billing
          </Button>
        </div>
      </div>
    </div>
  );
}
